<template>
  <div id="layerSwitcher">
    <div id="upperSection">
      <div>
        <label>
          {{ $t('layersSelector.dataLayer') }}
        </label>
        <b-field>
          <b-select :disabled="isUiDisabled()" @input="baseLayerGroupChanged" v-model="selectedBaseLayerGroup" icon="checkerboard">
            <option v-for="[key, content] in Object.entries(groupedBaseLayers)" :value="key" :key="key" :label="content.title">
            </option>
          </b-select>
        </b-field>
      </div>
      <div>
        <div id="settingsDiv" v-if="Object.keys(this.groupedBaseLayers[this.selectedBaseLayerGroup].params).length !== 0">
          <div v-for="[key, params] in Object.entries(this.groupedBaseLayers[this.selectedBaseLayerGroup].params)">
            <label>
              {{ $t(`layerSelector.layerParams.${key}`) }}
            </label>
            <b-field>
              <b-select :disabled="isUiDisabled() || (params.length === 1)" @input="selectedBaseLayerParamsChanged" v-model="selectedBaseLayerParams[key]" :id="key" expanded>
                <option v-for="opt in params" :value="opt" :key="opt" :label="opt">
                </option>
              </b-select>
            </b-field>
          </div>
        </div>
      </div>
    </div>
    <div id="splitter"></div>
    <div id="lowerSection">
      <div>
        <label>
          {{ $t('layersSelector.baseLayer') }}
        </label>
        <b-field>
          <b-select @input="overlayLayerChanged" v-model="checkedOverlayLayer" icon="earth" expanded>
            <option v-for="(layer, okey) in overlayLayersData" :value="okey" :key="okey" :label="layer.title">
            </option>
          </b-select>
        </b-field>
      </div>
    </div>
    <div id="compareButton">
      <b-button :disabled="isUiDisabled()" icon-left="select-compare" @click="addCurrentLayerToCompare()" expanded>{{ $t('addLayerToComparison') }}</b-button>
    </div>
  </div>
</template>

<script>

export default {
  props: {
    activeBaseLayer: {
      type: String,
      default: null
    },
    activeOverlayLayer: {
      type: String,
      default: null
    },
    baseLayers: {
      type: Object,
      default: null
    },
    overlayLayers: {
      type: Object,
      default: null
    },
    onBaseLayerChange: {
      type: Function
    },
    onOverlayLayerChange: {
      type: Function
    },
  },
  data() {
    return {
      selectedBaseLayerGroup: this.getSelectedBaseLayerGroup(),
      selectedBaseLayerParams: this.configureSelectedBaseLayerParams(),
      groupedBaseLayers: this.groupBaseLayers(),


      checkedOverlayLayer: this.activeOverlayLayer || '',
      baseLayersData: this.baseLayers,
      overlayLayersData: this.overlayLayers || {},
    }
  },
  methods: {
    getSelectedBaseLayerGroup() {
      return this.baseLayers[this.activeBaseLayer].layerType
    },
    baseLayerGroupChanged() {
      this.updateSelectedBaseLayerParams(this.groupedBaseLayers[this.selectedBaseLayerGroup].params)
      this.selectedBaseLayerParamsChanged()
    },
    configureSelectedBaseLayerParams() {
      let params = {}

      for (const [key, layer] of Object.entries(this.baseLayers)) {
        if (layer.hasOwnProperty('layerParams')) {
          Object.keys(layer['layerParams']).forEach(param => {
            params[param] = ""
          })
        }
      }

      // Fill defaults
      if (this.baseLayers[this.activeBaseLayer].hasOwnProperty('layerParams')) {
        for (const [param, value] of Object.entries(this.baseLayers[this.activeBaseLayer].layerParams)) {
          params[param] = value
        }
      }
      return params
    },
    updateSelectedBaseLayerParams(newParams) {
      for (const [param, value] of Object.entries(this.selectedBaseLayerParams)) {
        if (newParams.hasOwnProperty(param)) {
          // New param contains already existing value
          if (newParams[param].includes(this.selectedBaseLayerParams[param])) {

          } else {
            this.selectedBaseLayerParams[param] = newParams[param][0]
          }
        } else {
          this.selectedBaseLayerParams[param] = undefined
        }
      }
    },
    groupBaseLayers() {
      // Group base layers based on all layerType provided
      let groups = {}
      for (const layerName in this.baseLayers) {
        const layer = this.baseLayers[layerName]

        if (!groups.hasOwnProperty(layer.layerType)) {
          groups[layer.layerType] = {
            "title": layer.title,
            "params": {},
          }
        }
        
        if (layer.hasOwnProperty('layerParams')) {
          for (const [key, value] of Object.entries(layer['layerParams'])) {
            if (!groups[layer.layerType].params.hasOwnProperty(key)) {
              groups[layer.layerType].params[key] = []
            }
            if (!groups[layer.layerType].params[key].includes(value)) {
              groups[layer.layerType].params[key].push(value)
            }
          }
        }
      }
      return groups
    },
    selectedBaseLayerParamsChanged() {
      const effectiveParamKeys = Object.keys(this.groupedBaseLayers[this.selectedBaseLayerGroup].params)

      const preFilter = Object.values(this.baseLayers).filter((l) => {
        return l.layerType === this.selectedBaseLayerGroup
      })
      if (preFilter.length === 1) {
        this.baseLayerChanged(preFilter[0].id)
        return
      }

      const layer = preFilter.find((suspect) => {
        if (!suspect.hasOwnProperty('layerParams')) {
          return false
        }
        let match = []
        for (const effectiveKey of effectiveParamKeys) {
          match.push(this.selectedBaseLayerParams[effectiveKey] === suspect.layerParams[effectiveKey])          
        }
        if (match.includes(false)) {
          return false
        } else {
          return true
        }
      })

      if (layer === undefined) {
        return
      } else {
        this.baseLayerChanged(layer.id)
      }
    },
    baseLayerChanged(layer) {
      this.onBaseLayerChange(layer)
    },
    overlayLayerChanged() {
      this.onOverlayLayerChange(this.checkedOverlayLayer)
    },
    isUiDisabled() {
      return this.$store.getters['isComparisonModeActive']
    },
    addCurrentLayerToCompare () {
      this.$store.dispatch('addLayerToCompare')
    }
  },
}
</script>

<style lang="stylus" scoped>
#layerSwitcher
  position: absolute
  right: 41px
  top: 10px
  z-index: 1002
  border-radius: 5px
  overflow: hidden

#upperSection, #lowerSection
  background: #ffffff
  padding: 5px 10px 10px 10px
  display: flex
  flex-direction: column
  gap: 5px

  label
    height: fit-content

#upperSection
  border-radius: 5px 5px 0 0

#lowerSection
  border-radius: 0 0 5px 5px

#compareButton
  margin-top: 5px
  button
    border-radius: 5px

#settingsDiv
  display: grid
  grid-template-columns: repeat(2, minmax(0, 1fr))
  gap: 5px
  
  div
    text-align: left
    width: 100%

    div
      width: 100%
      
      span
        width: 100%

#splitter
  width: 100%
  height: 1px
  background: #ddd
</style>
